import { Trans, useTranslation } from 'react-i18next';

interface CrisisBannerProps {
  onDismiss?: () => void;
}

export default function CrisisBanner({ onDismiss }: CrisisBannerProps) {
  const { t } = useTranslation('crisis');

  return (
    <div className="crisis-banner bg-red-50 dark:bg-red-900/30 border-b-4 border-red-500 p-4" role="alert">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg
              className="w-6 h-6 text-red-600 dark:text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
              />
            </svg>
          </div>
          <div className="ml-3 flex-1">
            <h3 className="text-lg font-semibold text-red-800 dark:text-red-200">
              {t('supportAvailable')}
            </h3>
            <div className="mt-2 text-sm text-red-700 dark:text-red-300">
              <p className="mb-3">
                <Trans i18nKey="emergencyText" ns="crisis" components={{ strong: <strong /> }} />
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <ResourceLink
                  name={t('uelWellbeing')}
                  detail="020 8223 4444"
                  href="tel:02082234444"
                />
                <ResourceLink
                  name={t('samaritans')}
                  detail="116 123"
                  href="tel:116123"
                />
                <ResourceLink
                  name={t('shout')}
                  detail={t('shoutDetail')}
                  href="sms:85258?body=SHOUT"
                />
                <ResourceLink
                  name={t('studentMinds')}
                  detail="studentminds.org.uk"
                  href="https://www.studentminds.org.uk"
                />
              </div>
            </div>
          </div>
          {onDismiss && (
            <div className="ml-4">
              <button
                onClick={onDismiss}
                aria-label={t('dismissBanner')}
                className="p-1 rounded-full hover:bg-red-100 dark:hover:bg-red-800 transition-colors"
              >
                <svg
                  className="w-5 h-5 text-red-600 dark:text-red-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ResourceLink({
  name,
  detail,
  href,
}: {
  name: string;
  detail: string;
  href: string;
}) {
  const isPhone = href.startsWith('tel:');
  const isSms = href.startsWith('sms:');
  const actionLabel = isPhone
    ? `Call ${name} at ${detail}`
    : isSms
      ? `Text ${name} at ${detail}`
      : undefined;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={actionLabel}
      className="flex items-center p-2 bg-white dark:bg-gray-800 rounded-lg border border-red-200 dark:border-red-800 hover:border-red-400 dark:hover:border-red-600 transition-colors"
    >
      <div>
        <p className="font-medium text-red-800 dark:text-red-200">{name}</p>
        <p className="text-sm text-red-600 dark:text-red-400">{detail}</p>
      </div>
    </a>
  );
}
